<script setup>
import { ref, computed, onMounted } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { injectAccountModules } from 'dashboard/helpers/accountModules';

import SidebarGroup from './SidebarGroup.vue';
import Logo from 'next/icon/Logo.vue';
import { APP_VERSION } from 'dashboard/constants/globals';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute } = useAccount();
const store = useStore();
const { t } = useI18n();

const isRTL = useMapGetter('accounts/isRTL');
const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);
const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// ── Resize Handling ──
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;
  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

// ── Data Loading ──
onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get').catch(() => {});
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('segments/get');

  store.dispatch('crmConfig/get').then(() => {
    const crmConfig = store.getters['crmConfig/getCrmConfig'];
    if (crmConfig?.config?.modules) {
      injectAccountModules(crmConfig.config.modules);
    }
  });
  store.dispatch('captainInsights/get', { status: 'unread' }).catch(() => {});
});

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const toggleSidebar = () => {
  if (isCollapsed.value) {
    snapToExpanded();
  } else {
    snapToCollapsed();
  }
};

// ── Menu Sections (flat, no submenus) ──
const marketingItems = computed(() => [
  {
    name: 'Home',
    label: t('SIDEBAR.HOME'),
    icon: 'i-lucide-home',
    to: accountScopedRoute('dashboard_home'),
    activeOn: ['dashboard_home'],
  },
  {
    name: 'Conversations',
    label: t('SIDEBAR.CONVERSATIONS'),
    icon: 'i-lucide-message-circle',
    to: accountScopedRoute('home'),
    activeOn: [
      'home',
      'inbox_conversation',
      'conversation_through_mentions',
      'conversation_through_unattended',
      'conversations_through_label',
      'conversations_through_team',
      'conversation_through_inbox',
      'conversations_through_folders',
      'inbox_view',
      'inbox_view_conversation',
    ],
  },
  {
    name: 'Contacts',
    label: t('SIDEBAR.CONTACTS'),
    icon: 'i-lucide-contact',
    to: accountScopedRoute(
      'contacts_dashboard_index',
      {},
      { page: 1, search: undefined }
    ),
    activeOn: [
      'contacts_dashboard_index',
      'contacts_edit',
      'contacts_dashboard_active',
      'contacts_dashboard_labels_index',
    ],
  },
  {
    name: 'Segments',
    label: t('SIDEBAR.SEGMENTS'),
    icon: 'i-lucide-pie-chart',
    to: accountScopedRoute('segments_index'),
    activeOn: ['segments_index', 'segment_detail'],
  },
  {
    name: 'Conversions',
    label: t('SIDEBAR.CONVERSIONS'),
    icon: 'i-lucide-zap',
    to: accountScopedRoute('conversions_index'),
    activeOn: ['conversions_index', 'conversion_detail'],
  },
  {
    name: 'Forms',
    label: t('SIDEBAR.FORMS'),
    icon: 'i-lucide-file-text',
    to: accountScopedRoute('forms_index'),
    activeOn: ['forms_index', 'forms_new', 'forms_edit', 'forms_submissions'],
  },
]);

const crmItems = computed(() => [
  {
    name: 'CRM',
    label: t('SIDEBAR.CRM'),
    icon: 'i-lucide-kanban',
    to: accountScopedRoute('crm_board'),
    activeOn: ['crm_board', 'crm_item_detail', 'crm_list', 'crm_calendar'],
  },
  {
    name: 'Products',
    label: t('SIDEBAR.PRODUCTS'),
    icon: 'i-lucide-package',
    to: accountScopedRoute('crm_products'),
    activeOn: ['crm_products', 'crm_product_new', 'crm_product_edit'],
  },
  {
    name: 'Tasks',
    label: t('SIDEBAR.TASKS'),
    icon: 'i-lucide-check-square',
    to: accountScopedRoute('crm_tasks'),
    activeOn: ['crm_tasks'],
  },
  {
    name: 'Tickets',
    label: t('SIDEBAR.TICKETS'),
    icon: 'i-lucide-ticket',
    to: accountScopedRoute('crm_tickets_board'),
    activeOn: [
      'crm_tickets_board',
      'crm_ticket_detail',
      'crm_tickets_list',
      'crm_tickets_calendar',
    ],
  },
  {
    name: 'Companies',
    label: t('SIDEBAR.COMPANIES'),
    icon: 'i-lucide-building-2',
    to: accountScopedRoute(
      'companies_dashboard_index',
      {},
      { page: 1, search: undefined }
    ),
    activeOn: ['companies_dashboard_index', 'companies_detail'],
  },
]);

const socialItems = computed(() => [
  {
    name: 'Social',
    label: t('SIDEBAR.SOCIAL'),
    icon: 'i-lucide-at-sign',
    to: accountScopedRoute('social_dashboard'),
    activeOn: [
      'social_dashboard',
      'social_comments',
      'social_automations',
      'social_settings',
    ],
  },
  {
    name: 'Google Reviews',
    label: t('SIDEBAR.GOOGLE_REVIEWS'),
    icon: 'i-lucide-star',
    to: accountScopedRoute('social_google_reviews'),
    activeOn: ['social_google_reviews'],
  },
]);

const toolItems = computed(() => [
  {
    name: 'Canned Responses',
    label: t('SIDEBAR.CANNED_RESPONSES'),
    icon: 'i-lucide-message-square-quote',
    to: accountScopedRoute('canned_list'),
    activeOn: ['canned_list'],
  },
  {
    name: 'Macros',
    label: t('SIDEBAR.MACROS'),
    icon: 'i-lucide-toy-brick',
    to: accountScopedRoute('macros_wrapper'),
    activeOn: ['macros_wrapper'],
  },
  {
    name: 'Labels',
    label: t('SIDEBAR.LABELS'),
    icon: 'i-lucide-tags',
    to: accountScopedRoute('labels_list'),
    activeOn: ['labels_list'],
  },
]);

const captainItems = computed(() => [
  {
    name: 'Insights',
    label: t('SIDEBAR.BRAIN.INSIGHTS'),
    icon: 'i-lucide-lightbulb',
    to: accountScopedRoute('brain_insights'),
    activeOn: ['brain_insights'],
    getterKeys: {
      count: 'captainInsights/getUnreadCount',
    },
  },
]);
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="s2-sidebar bg-n-s2-nav-bg flex flex-col text-sm pb-0.5 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[220px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 shadow-[1px_0_8px_rgba(0,0,0,0.04)] dark:shadow-[1px_0_8px_rgba(0,0,0,0.3)]"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <!-- Header: Logo + Brand (click → Home) -->
    <section class="flex-shrink-0">
      <RouterLink
        :to="accountScopedRoute('dashboard_home')"
        class="flex items-center min-w-0 h-12 cursor-pointer hover:bg-n-s2-nav-bg-hover transition-colors no-underline"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'gap-2.5 px-3': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <Logo class="size-7" />
        </template>
        <template v-else>
          <Logo class="size-8 flex-shrink-0" />
          <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
          <span
            class="sidebar-brand-text text-base font-bold text-n-s2-nav-text-hover whitespace-nowrap overflow-hidden"
          >
            app<span class="sidebar-brand-dot">.</span>smart
          </span>
          <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        </template>
      </RouterLink>
    </section>

    <!-- Nav: Flat Menu Sections -->
    <nav
      class="flex-1 overflow-y-auto no-scrollbar min-w-0 pt-2"
      :class="isEffectivelyCollapsed ? 'px-1' : ''"
    >
      <!-- MARKETING Section -->
      <div v-if="!isEffectivelyCollapsed" class="px-4 pt-2.5 pb-1">
        <span
          class="text-[9px] font-semibold uppercase tracking-[1.2px] text-n-s2-nav-section"
        >
          {{ $t('SIDEBAR.SECTION_MARKETING') }}
        </span>
      </div>
      <ul
        class="flex flex-col m-0 list-none min-w-0"
        :class="{ 'items-center gap-0.5': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in marketingItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>

      <!-- CRM Section -->
      <div v-if="!isEffectivelyCollapsed" class="px-4 pt-4 pb-1">
        <span
          class="text-[9px] font-semibold uppercase tracking-[1.2px] text-n-s2-nav-section"
        >
          {{ $t('SIDEBAR.CRM') }}
        </span>
      </div>
      <div v-else class="flex justify-center py-2">
        <div class="w-5 border-t border-n-s2-nav-border" />
      </div>
      <ul
        class="flex flex-col m-0 list-none min-w-0"
        :class="{ 'items-center gap-0.5': isEffectivelyCollapsed }"
      >
        <SidebarGroup v-for="item in crmItems" :key="item.name" v-bind="item" />
      </ul>

      <!-- SOCIAL Section -->
      <div v-if="!isEffectivelyCollapsed" class="px-4 pt-4 pb-1">
        <span
          class="text-[9px] font-semibold uppercase tracking-[1.2px] text-n-s2-nav-section"
        >
          {{ $t('SIDEBAR.SECTION_SOCIAL') }}
        </span>
      </div>
      <div v-else class="flex justify-center py-2">
        <div class="w-5 border-t border-n-s2-nav-border" />
      </div>
      <ul
        class="flex flex-col m-0 list-none min-w-0"
        :class="{ 'items-center gap-0.5': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in socialItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>

      <!-- FERRAMENTAS Section -->
      <div v-if="!isEffectivelyCollapsed" class="px-4 pt-4 pb-1">
        <span
          class="text-[9px] font-semibold uppercase tracking-[1.2px] text-n-s2-nav-section"
        >
          {{ $t('SIDEBAR.SECTION_TOOLS') }}
        </span>
      </div>
      <div v-else class="flex justify-center py-2">
        <div class="w-5 border-t border-n-s2-nav-border" />
      </div>
      <ul
        class="flex flex-col m-0 list-none min-w-0"
        :class="{ 'items-center gap-0.5': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in toolItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>

      <!-- BRAIN Section -->
      <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      <div v-if="!isEffectivelyCollapsed" class="px-4 pt-4 pb-1">
        <span
          class="text-[9px] uppercase tracking-[1.2px] text-n-s2-nav-section brain-wordmark"
        >
          br<span class="brain-highlight">ai</span>n
        </span>
      </div>
      <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      <div v-else class="flex justify-center py-2">
        <div class="w-5 border-t border-n-s2-nav-border" />
      </div>
      <ul
        class="flex flex-col m-0 list-none min-w-0"
        :class="{ 'items-center gap-0.5': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in captainItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>

    <!-- Bottom: Toggle Button -->
    <section class="flex relative flex-col flex-shrink-0 items-center">
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-s2-nav-bg to-transparent"
      />
      <div class="p-1.5 flex-shrink-0 w-full border-t border-n-s2-nav-border">
        <button
          class="flex items-center w-full px-2 py-1.5 rounded-lg text-xs text-n-s2-nav-text hover:bg-n-s2-nav-bg-hover hover:text-n-s2-nav-text-hover transition-colors cursor-pointer"
          :class="isEffectivelyCollapsed ? 'justify-center' : 'gap-2'"
          :title="
            isEffectivelyCollapsed
              ? $t('SIDEBAR.EXPAND_MENU')
              : $t('SIDEBAR.COLLAPSE_MENU')
          "
          @click="toggleSidebar"
        >
          <span
            class="size-4 flex-shrink-0 transition-transform duration-200"
            :class="
              isEffectivelyCollapsed
                ? 'i-lucide-chevrons-right'
                : 'i-lucide-chevrons-left'
            "
          />
          <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
          <span
            v-if="!isEffectivelyCollapsed"
            class="text-n-s2-nav-text flex-1"
          >
            {{ $t('SIDEBAR.COLLAPSE') }}
          </span>
          <span
            class="text-n-s2-nav-muted text-[10px] tracking-wide select-none"
          >
            v{{ APP_VERSION }}
          </span>
          <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
        </button>
      </div>
    </section>

    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-s2-nav-border transition-colors"
        :class="{ 'bg-n-s2-nav-muted': isResizing }"
      />
    </div>
  </aside>
</template>

<style>
/* Smart2 warm sidebar overrides for child components */

/* Text colors: active → dark brown, inactive → warm gray */
.s2-sidebar .text-n-slate-12 {
  color: rgb(var(--s2-nav-text-hover)) !important;
}
.s2-sidebar .text-n-slate-11 {
  color: rgb(var(--s2-nav-text)) !important;
}
.s2-sidebar .text-n-slate-10 {
  color: rgb(var(--s2-nav-muted)) !important;
}
.s2-sidebar .text-body-main {
  color: rgb(var(--s2-nav-text)) !important;
}
.s2-sidebar .\!text-n-slate-11 {
  color: rgb(var(--s2-nav-text)) !important;
}

/* Background overrides */
.s2-sidebar .bg-n-alpha-2 {
  background-color: rgb(var(--s2-nav-bg-active)) !important;
}
.s2-sidebar .bg-n-alpha-1 {
  background-color: rgb(var(--s2-nav-bg-hover) / 0.5) !important;
}
.s2-sidebar .hover\:bg-n-alpha-2:hover {
  background-color: rgb(var(--s2-nav-bg-hover)) !important;
}
.s2-sidebar .hover\:bg-n-alpha-1:hover {
  background-color: rgb(var(--s2-nav-bg-hover) / 0.5) !important;
}

/* Border overrides */
.s2-sidebar .border-n-weak {
  border-color: rgb(var(--s2-nav-border)) !important;
}
.s2-sidebar .border-n-strong {
  border-color: rgb(var(--s2-nav-border)) !important;
}

/* Active item gradient left accent */
.s2-sidebar nav li > a.bg-n-alpha-2,
.s2-sidebar nav li > div.bg-n-alpha-2 {
  position: relative;
  background-color: rgb(var(--s2-nav-bg-active)) !important;
}
.s2-sidebar nav li > a.bg-n-alpha-2::before,
.s2-sidebar nav li > div.bg-n-alpha-2::before {
  content: '';
  position: absolute;
  left: 0;
  top: 7px;
  bottom: 7px;
  width: 3px;
  background: linear-gradient(
    135deg,
    rgb(var(--brand-primary)) 0%,
    rgb(var(--brand-secondary)) 100%
  );
  border-radius: 0 3px 3px 0;
}

/* Active item text → brand color */
.s2-sidebar nav li > a.bg-n-alpha-2 .text-n-slate-12,
.s2-sidebar nav li > div.bg-n-alpha-2 .text-n-slate-12 {
  color: rgb(var(--s2-nav-text-active)) !important;
}

/* Hover state */
.s2-sidebar nav li > a[role='button']:hover,
.s2-sidebar nav li > div[role='button']:hover {
  color: rgb(var(--s2-nav-text-hover)) !important;
  background-color: rgb(var(--s2-nav-bg-hover)) !important;
}

/* Collapsed active item accent */
.s2-sidebar nav li .size-10.bg-n-alpha-2 {
  position: relative;
}
.s2-sidebar nav li .size-10.bg-n-alpha-2::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  background: linear-gradient(
    135deg,
    rgb(var(--brand-primary)) 0%,
    rgb(var(--brand-secondary)) 100%
  );
  border-radius: 0 3px 3px 0;
}

/* Brand text with gradient dot */
.sidebar-brand-dot {
  background: linear-gradient(
    135deg,
    rgb(var(--brand-primary)) 0%,
    rgb(var(--brand-secondary)) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ============================
   Item sizing (match prototype)
   ============================ */

/* Expanded sidebar items: height 38px, gap 12px, padding 0 16px */
.s2-sidebar nav li > a[role='button'],
.s2-sidebar nav li > div[role='button'] {
  gap: 12px !important;
  padding: 0 16px !important;
  height: 38px !important;
  border-radius: 0 !important;
}

/* Active item: no rounding + gradient accent */
.s2-sidebar nav li > a.bg-n-alpha-2,
.s2-sidebar nav li > div.bg-n-alpha-2 {
  border-radius: 0 !important;
}

/* Icon size: 18px */
.s2-sidebar nav li [role='button'] .size-4 {
  width: 18px !important;
  height: 18px !important;
}

/* Label text: 13px, weight 500 */
.s2-sidebar nav li [role='button'] .truncate {
  font-size: 13px !important;
  font-weight: 500 !important;
}

/* Active label stays medium weight */
.s2-sidebar nav li [role='button'] .font-medium .truncate,
.s2-sidebar nav li [role='button'] .truncate.font-medium {
  font-weight: 500 !important;
}

/* Collapsed state: icon container size-10 (40px), icon 18px */
.s2-sidebar nav li .size-10 .size-4 {
  width: 18px !important;
  height: 18px !important;
}

/* Sidebar child tree lines */
.s2-sidebar .sidebar-group-children .child-item::before {
  background-color: rgb(var(--s2-nav-border));
}
.s2-sidebar .sidebar-group-children .child-item::after {
  border-color: rgb(var(--s2-nav-border));
}
</style>
